import { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { ref, uploadBytes } from "firebase/storage";
import { auth, storage } from "../../firebase";
import { analyzeMenu, commitMenuImport } from "../../utility/aiApi";
import "./AiMenuImport.scss";

const MAX_FILES = 20;
const MAX_BYTES = 15 * 1024 * 1024;

const move = (items, index, direction) => {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) return items;
  const next = [...items];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
};

export default function AiMenuImport({ show, onHide, orgId, onCommitted, t, alert }) {
  const [files, setFiles] = useState([]);
  const [importId, setImportId] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [generateIcons, setGenerateIcons] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedCount = useMemo(
    () => candidates.filter((candidate) => candidate.action !== "skip").length,
    [candidates],
  );

  const reset = () => {
    setFiles([]);
    setImportId("");
    setCandidates([]);
    setCategories([]);
    setGenerateIcons(false);
  };

  const close = () => {
    if (!loading) {
      reset();
      onHide();
    }
  };

  const addFiles = (event) => {
    const incoming = Array.from(event.target.files || []);
    event.target.value = "";
    const next = [...files, ...incoming].slice(0, MAX_FILES);
    if (next.reduce((sum, file) => sum + file.size, 0) > MAX_BYTES) {
      alert.show(t("ai_menu_too_large"), { type: "error" });
      return;
    }
    setFiles(next);
  };

  const analyze = async () => {
    if (!files.length || !orgId || !auth.currentUser) return;
    setLoading(true);
    try {
      const nextImportId = crypto.randomUUID();
      const descriptors = [];
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
        const storagePath = `ai-imports/${orgId}/${auth.currentUser.uid}/${nextImportId}/${String(index + 1).padStart(2, "0")}-${safeName}`;
        await uploadBytes(ref(storage, storagePath), file, { contentType: file.type });
        descriptors.push({
          storagePath,
          mimeType: file.type,
          size: file.size,
          fileName: file.name,
        });
      }
      const result = await analyzeMenu({ orgId, importId: nextImportId, files: descriptors });
      setImportId(result.importId);
      setCategories(result.categories || []);
      setCandidates(result.candidates || []);
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const updateCandidate = (id, changes) =>
    setCandidates((current) =>
      current.map((candidate) => (candidate.id === id ? { ...candidate, ...changes } : candidate)),
    );

  const commit = async () => {
    setLoading(true);
    try {
      await commitMenuImport(
        importId,
        candidates.map((candidate) => ({
          id: candidate.id,
          title: candidate.title,
          price: candidate.price,
          category: candidate.category,
          action: candidate.action,
          productId: candidate.duplicate?.id,
        })),
        generateIcons,
      );
      await onCommitted();
      alert.show(t(generateIcons ? "ai_menu_committed" : "ai_menu_committed_without_icons"), {
        type: "success",
      });
      reset();
      onHide();
    } catch (error) {
      alert.show(error.message, { type: "error" });
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={close} centered size="xl" className="ai-menu-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t("ai_menu_title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="ai-menu-loading" role="status" aria-live="polite">
            <span className="ai-menu-spinner" aria-hidden="true" />
            <strong>{importId ? t("ai_menu_saving") : t("ai_menu_analyzing")}</strong>
            <small>{t("ai_menu_analysis_wait")}</small>
          </div>
        ) : !importId ? (
          <>
            <p>{t("ai_menu_help")}</p>
            <label className="ai-menu-picker">
              {t("ai_menu_choose")}
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={addFiles}
              />
            </label>
            <div className="ai-menu-pages">
              {files.map((file, index) => (
                <div className="ai-menu-page" key={`${file.name}-${file.lastModified}-${index}`}>
                  <span>
                    {index + 1}. {file.name}
                  </span>
                  <div>
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => setFiles(move(files, index, -1))}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      disabled={index === files.length - 1}
                      onClick={() => setFiles(move(files, index, 1))}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => setFiles(files.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="ai-menu-review">
            <p>{t("ai_menu_review")}</p>
            <label className="ai-menu-icon-option">
              <input
                type="checkbox"
                checked={generateIcons}
                onChange={(event) => setGenerateIcons(event.target.checked)}
              />
              <span>
                <strong>{t("ai_menu_generate_icons")}</strong>
                <small>{t("ai_menu_generate_icons_help")}</small>
              </span>
            </label>
            <div className="ai-menu-columns" aria-hidden="true">
              <span>{t("ai_menu_column_select")}</span>
              <span>{t("ai_menu_column_product")}</span>
              <span>{t("ai_menu_column_price")}</span>
              <span>{t("ai_menu_column_category")}</span>
              <span>{t("ai_menu_column_source")}</span>
              <span>{t("ai_menu_column_confidence")}</span>
              <span>{t("ai_menu_column_action")}</span>
            </div>
            {candidates.map((candidate) => (
              <div
                className={`ai-menu-candidate ${candidate.duplicate ? "is-duplicate" : ""}`}
                key={candidate.id}
              >
                <input
                  type="checkbox"
                  checked={candidate.action !== "skip"}
                  onChange={(event) =>
                    updateCandidate(candidate.id, {
                      action: event.target.checked ? "create" : "skip",
                    })
                  }
                />
                <input
                  value={candidate.title}
                  onChange={(event) => updateCandidate(candidate.id, { title: event.target.value })}
                />
                <input
                  className="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={candidate.price ?? ""}
                  onChange={(event) => updateCandidate(candidate.id, { price: event.target.value })}
                />
                <select
                  value={candidate.category || ""}
                  onChange={(event) =>
                    updateCandidate(candidate.id, { category: event.target.value })
                  }
                >
                  <option value="">{t("ai_menu_confirm_category")}</option>
                  {categories.map((category) => (
                    <option key={category.title} value={category.title}>
                      {category.title}
                    </option>
                  ))}
                </select>
                <span className="source">
                  {t("ai_menu_page")} {candidate.pageSource}
                </span>
                <span className="confidence">
                  {Math.round(
                    Math.min(
                      candidate.titleConfidence,
                      candidate.priceConfidence,
                      candidate.categoryConfidence,
                    ) * 100,
                  )}
                  %
                </span>
                {candidate.duplicate && (
                  <select
                    value={candidate.action}
                    onChange={(event) =>
                      updateCandidate(candidate.id, { action: event.target.value })
                    }
                  >
                    <option value="skip">{t("ai_menu_skip")}</option>
                    <option value="create">{t("ai_menu_force_create")}</option>
                    <option value="update">{t("ai_menu_update")}</option>
                  </select>
                )}
                {!candidate.duplicate ? <span className="candidate-action-empty">—</span> : null}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="ai-menu-cancel" disabled={loading} onClick={close}>
          {t("cancel")}
        </button>
        {!importId ? (
          <button
            type="button"
            className="themeBtn"
            disabled={loading || !files.length}
            onClick={analyze}
          >
            {loading ? t("ai_menu_analyzing") : t("ai_menu_analyze")}
          </button>
        ) : (
          <button
            type="button"
            className="themeBtn"
            disabled={loading || selectedCount === 0}
            onClick={commit}
          >
            {loading ? t("ai_menu_saving") : `${t("ai_menu_create")} (${selectedCount})`}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
